<template>
  <div class="chat-thread">
    <article
      v-for="message in messages"
      :key="message.id"
      class="chat-thread__message"
      :class="`chat-thread__message--${message.role}`"
    >
      <div v-if="message.role === 'assistant'" class="chat-thread__assistant-icon">
        <BaseIcon name="wand" size="14" />
      </div>

      <div class="chat-thread__bubble" :class="`chat-thread__bubble--${message.role}`">
        <div
          v-if="message.role === 'assistant' && message.content"
          class="chat-thread__content"
          v-html="renderMarkdown(message.content)"
        />
        <div v-else-if="message.role === 'user'" class="chat-thread__content chat-thread__content--user">
          {{ message.content }}
        </div>

        <template v-if="message.role === 'assistant' && message.tool_results?.length">
          <ChatToolResult
            v-for="(tr, idx) in message.tool_results"
            :key="tr.tool_call_id || `${tr.tool}-${idx}`"
            :result="tr"
          />
        </template>

        <template v-if="message.role === 'assistant' && message.pending_actions?.length">
          <HomeChatActionCard
            v-for="action in message.pending_actions"
            :key="action.id"
            :action="action"
            @confirm="$emit('confirm-action', $event)"
            @dismiss="$emit('dismiss-action', $event)"
          />
        </template>
      </div>
    </article>

    <article v-if="sending" class="chat-thread__message chat-thread__message--assistant">
      <div class="chat-thread__assistant-icon">
        <BaseIcon name="wand" size="14" />
      </div>
      <div class="chat-thread__bubble chat-thread__bubble--assistant">
        <div class="chat-thread__typing">
          <span></span><span></span><span></span>
        </div>
      </div>
    </article>
  </div>
</template>

<script>
  import { onMounted, onUpdated, getCurrentInstance } from 'vue';
  import { useRouter } from 'vue-router';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import HomeChatActionCard from '@/components/dashboard/HomeChatActionCard.vue';
  import ChatToolResult from '@/components/dashboard/ChatToolResult.vue';

  // Minimal safe markdown renderer for the spike. Supports:
  //   **bold**          → <strong>
  //   *italic* / _italic_ → <em>
  //   [text](href)      → internal router-link or external <a>
  //   line breaks       → <br>, double newline → paragraph
  //   - item            → <li> within <ul>
  function escapeHtml(str) {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function renderInline(line) {
    let html = escapeHtml(line);
    // Links — must run before bold/italic so escaped underscores don't interfere
    html = html.replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      (_match, label, href) => {
        const isInternal = href.startsWith('/');
        const cls = 'chat-link';
        if (isInternal) {
          return `<a href="${href}" class="${cls}" data-internal="1">${label}</a>`;
        }
        return `<a href="${href}" class="${cls}" target="_blank" rel="noopener noreferrer">${label}</a>`;
      }
    );
    // Bold
    html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    // Italic (avoid matching inside words)
    html = html.replace(/(^|\s)\*([^*\s][^*]*[^*\s]|[^*\s])\*(?=\s|$|[.,!?])/g, '$1<em>$2</em>');
    html = html.replace(/(^|\s)_([^_\s][^_]*[^_\s]|[^_\s])_(?=\s|$|[.,!?])/g, '$1<em>$2</em>');
    return html;
  }

  function renderMarkdown(text) {
    if (!text) return '';
    const lines = text.split('\n');
    const out = [];
    let listOpen = false;
    let paragraph = [];

    const flushParagraph = () => {
      if (paragraph.length) {
        out.push(`<p>${paragraph.join('<br>')}</p>`);
        paragraph = [];
      }
    };

    const closeList = () => {
      if (listOpen) {
        out.push('</ul>');
        listOpen = false;
      }
    };

    for (const raw of lines) {
      const line = raw.trim();
      if (!line) {
        flushParagraph();
        closeList();
        continue;
      }
      if (line.startsWith('- ')) {
        flushParagraph();
        if (!listOpen) {
          out.push('<ul>');
          listOpen = true;
        }
        out.push(`<li>${renderInline(line.slice(2))}</li>`);
      } else {
        closeList();
        paragraph.push(renderInline(line));
      }
    }
    flushParagraph();
    closeList();
    return out.join('');
  }

  export default {
    name: 'HomeChatThread',

    components: { BaseIcon, HomeChatActionCard, ChatToolResult },

    props: {
      messages: { type: Array, default: () => [] },
      sending: { type: Boolean, default: false },
    },

    emits: ['confirm-action', 'dismiss-action'],

    setup() {
      const instance = getCurrentInstance();
      const router = useRouter();

      // Intercept clicks on internal links and route via vue-router instead
      // of doing a full page reload.
      const interceptLinks = () => {
        const el = instance?.proxy?.$el;
        if (!el) return;
        el.querySelectorAll('a[data-internal="1"]').forEach((a) => {
          if (a.dataset.bound) return;
          a.dataset.bound = '1';
          a.addEventListener('click', (e) => {
            e.preventDefault();
            const href = a.getAttribute('href');
            if (href) router.push(href);
          });
        });
      };

      // Auto-scroll to bottom whenever new content arrives.
      const scrollToBottom = () => {
        const el = instance?.proxy?.$el;
        if (!el) return;
        const scroller = el.closest('.chat-thread-scroll') || el;
        scroller.scrollTop = scroller.scrollHeight;
      };

      onMounted(() => {
        interceptLinks();
        scrollToBottom();
      });

      onUpdated(() => {
        interceptLinks();
        scrollToBottom();
      });

      return { renderMarkdown };
    },
  };
</script>

<style scoped lang="scss">
  @import '@/assets/scss/base/_mixins.scss';

  .chat-thread {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 0.5rem 0;

    &__message {
      display: flex;
      align-items: flex-start;
      gap: 0.625rem;

      &--user {
        flex-direction: row-reverse;
      }
    }

    &__assistant-icon {
      width: 24px;
      height: 24px;
      border-radius: 9999px;
      background: linear-gradient(135deg, var(--brand-50), var(--brand-100));
      color: var(--brand-600);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      margin-top: 0.125rem;
    }

    &__bubble {
      max-width: min(720px, calc(100% - 36px));
      min-width: 0;

      &--user {
        background: var(--brand-500);
        color: white;
        padding: 0.5rem 0.875rem;
        border-radius: 1rem 1rem 0.25rem 1rem;
      }

      &--assistant {
        background: transparent;
        padding: 0.125rem 0;
      }
    }

    &__content {
      font-size: 0.9375rem;
      line-height: 1.55;
      color: var(--slate-800);
      word-wrap: break-word;

      &--user {
        color: white;
      }

      :deep(p) {
        margin: 0 0 0.625rem 0;
        &:last-child {
          margin-bottom: 0;
        }
      }

      :deep(ul) {
        margin: 0.25rem 0 0.625rem 0;
        padding-left: 1.25rem;
        display: flex;
        flex-direction: column;
        gap: 0.25rem;

        &:last-child {
          margin-bottom: 0;
        }
      }

      :deep(li) {
        line-height: 1.5;
      }

      :deep(strong) {
        color: var(--slate-900);
        font-weight: 600;
      }

      :deep(.chat-link) {
        color: var(--brand-700);
        text-decoration: none;
        background: var(--brand-50);
        padding: 0.0625rem 0.375rem;
        border-radius: 0.25rem;
        font-weight: 500;
        transition: all 0.15s ease;
        cursor: pointer;
        white-space: nowrap;

        @include hover {
          background: var(--brand-100);
          color: var(--brand-800);
        }
      }
    }

    &__tools {
      display: flex;
      flex-wrap: wrap;
      gap: 0.25rem;
      margin-top: 0.5rem;
      padding-top: 0.5rem;
      border-top: 1px dashed var(--slate-200);
    }

    &__tool {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      padding: 0.0625rem 0.375rem;
      border-radius: 0.25rem;
      background: var(--slate-100);
      color: var(--slate-500);
      font-size: 0.6875rem;
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;

      .icon {
        color: var(--amber-500);
      }
    }

    &__typing {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      padding: 0.625rem 0.875rem;
      background: var(--slate-100);
      border-radius: 1rem;

      span {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: var(--slate-400);
        animation: typing 1.4s infinite ease-in-out;

        &:nth-child(2) {
          animation-delay: 0.2s;
        }

        &:nth-child(3) {
          animation-delay: 0.4s;
        }
      }
    }
  }

  @keyframes typing {
    0%,
    60%,
    100% {
      transform: translateY(0);
      opacity: 0.4;
    }
    30% {
      transform: translateY(-4px);
      opacity: 1;
    }
  }
</style>
